/**
 * Helpers for partial flight-change flows (return / multi-city).
 */

export function sortFlightsByDeparture(flights) {
  return [...(flights || [])].sort(
    (a, b) =>
      new Date(a.departure?.datetime || a.departure_date_time).getTime() -
      new Date(b.departure?.datetime || b.departure_date_time).getTime()
  );
}

export function parseChangeLegTokensFromQuery(raw) {
  if (!raw) return [];
  const value = Array.isArray(raw) ? raw.join(',') : String(raw);
  return value
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
}

export function getPartialChangeContext(originalFlights, changeLegTokens) {
  const sortedOriginalFlights = sortFlightsByDeparture(originalFlights);
  const tokens = changeLegTokens || [];

  if (!sortedOriginalFlights.length || !tokens.length) {
    return {
      isPartialChange: false,
      changingLegIndices: [],
      lockedSegmentIndices: [],
      editableSegmentIndices: sortedOriginalFlights.map((_, i) => i),
      changingOutbound: true,
      changingReturn: true,
      sortedOriginalFlights,
    };
  }

  const changeSet = new Set(tokens);
  const changingLegIndices = sortedOriginalFlights
    .map((leg, index) => (changeSet.has(leg.booking_token) ? index : null))
    .filter((index) => index !== null);

  const isPartialChange =
    changingLegIndices.length > 0 &&
    changingLegIndices.length < sortedOriginalFlights.length;

  const lockedSegmentIndices = sortedOriginalFlights
    .map((_, index) => index)
    .filter((index) => !changingLegIndices.includes(index));

  return {
    isPartialChange,
    changingLegIndices,
    lockedSegmentIndices,
    editableSegmentIndices: changingLegIndices,
    changingOutbound: changingLegIndices.includes(0),
    changingReturn: changingLegIndices.includes(1),
    sortedOriginalFlights,
  };
}

/** Map a booking leg to a flight-selection shape (preserved / unchanged leg). */
export function legToSelectableFlight(leg) {
  const depDt = leg.departure?.datetime || leg.departure_date_time;
  const arrDt = leg.arrival?.datetime || leg.arrival_date_time;
  return {
    id: leg.routes_id,
    routes_id: leg.routes_id,
    flight_number: leg.flight_number,
    flightNumber: leg.flight_number,
    aircraft_id: leg.aircraft_name,
    registration_number: leg.registration_number,
    departure_airport: leg.departure?.id || leg.departure_airport,
    arrival_airport: leg.arrival?.id || leg.arrival_airport,
    departure_date_time: depDt,
    arrival_date_time: arrDt,
    departure: {
      datetime: depDt,
      airport_code: leg.departure?.iata || leg.departure_airport_code || '',
    },
    arrival: {
      datetime: arrDt,
      airport_code: leg.arrival?.iata || leg.arrival_airport_code || '',
    },
    _fromOriginalBooking: true,
    booking_token: leg.booking_token,
    availableSeats: leg.available_seats ?? leg.remaining_seats,
    price: leg.paid_total_amount ?? leg.display_total_fare,
  };
}

export function findMatchingSearchFlight(searchFlights, leg) {
  if (!searchFlights?.length || !leg) return null;
  const routeId = leg.routes_id || leg.id;
  const depId = leg.departure?.id || leg.departure_airport;
  const depIata = leg.departure?.iata;
  return (
    searchFlights.find(
      (f) =>
        f.id === routeId ||
        f.routes_id === routeId ||
        (f.flight_number === leg.flight_number &&
          (String(f.departure_airport) === String(depId) ||
            (depIata &&
              (f.departure?.airport_code === depIata ||
                f.departure?.airportCode === depIata))))
    ) || null
  );
}

/** Aircraft route id required by GET /flights/search/return */
export function resolveOutboundRouteId(flight, originalLeg, searchFlights) {
  const fromSearch =
    flight?.id && !flight._fromOriginalBooking ? flight.id : null;
  if (fromSearch) return fromSearch;

  const fromLeg = originalLeg?.routes_id || flight?.routes_id;
  if (fromLeg) return fromLeg;

  const matched = findMatchingSearchFlight(searchFlights, originalLeg || flight);
  return matched?.id ?? null;
}
